import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { Auth } from '@aws-amplify/auth';
import jwt from 'jwt-decode';
import { clearAuthTokenCache } from '../util/authUtil';

/**
 * AuthContext - Secure In-Memory Token Storage
 * 
 * SECURITY FEATURES:
 * - Tokens stored ONLY in React state (memory)
 * - NO localStorage, sessionStorage, or cookies used for tokens
 * - Tokens are lost on page refresh (intentional for security)
 * - Not accessible via browser DevTools Console
 * 
 * HIPAA COMPLIANCE:
 * - No PHI-related identifiers persisted on client
 * - Session expires after 15 minutes of inactivity
 * - Clean logout clears all auth state
 */

const AuthContext = createContext(null);

// Token expiry buffer (refresh tokens 2 minutes before expiry)
const TOKEN_EXPIRY_BUFFER_SECONDS = 120;

/**
 * Custom hook to access auth context
 * @throws Error if used outside of AuthProvider
 */
export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

/**
 * AuthProvider Component
 * Wraps the application and provides secure auth state management
 */
export const AuthProvider = ({ children }) => {
    // In-memory token storage - NEVER persisted to localStorage/cookies
    const [tokens, setTokens] = useState(null);
    const [user, setUser] = useState(null);
    const [userGroups, setUserGroups] = useState([]);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [authError, setAuthError] = useState(null);
    
    // Ref to track if component is mounted (avoid state updates after unmount)
    const isMounted = useRef(true);
    // Prevent multiple simultaneous session checks (e.g. Strict Mode double-mount)
    const sessionCheckInProgress = useRef(false);

    /**
     * Extract user information from ID token
     */
    const extractUserFromToken = useCallback((idToken) => {
        try {
            const decoded = jwt(idToken);
            return {
                sub: decoded.sub,
                email: decoded.email,
                given_name: decoded.given_name,
                family_name: decoded.family_name,
                groups: decoded['cognito:groups'] || [],
                exp: decoded.exp,
                iat: decoded.iat
            };
        } catch (err) {
            console.error('Error decoding token:', err);
            return null;
        }
    }, []);

    /**
     * Store tokens in memory after successful authentication
     * This is the ONLY place tokens are stored - in React state
     */
    const storeTokens = useCallback((session) => {
        if (!isMounted.current) return;
        
        try {
            const idToken = session.getIdToken().getJwtToken();
            const accessToken = session.getAccessToken().getJwtToken();
            const idTokenPayload = session.getIdToken().payload;
            const accessTokenPayload = session.getAccessToken().payload;
            
            // Store tokens in memory only
            setTokens({
                idToken,
                accessToken,
                idTokenExp: idTokenPayload.exp,
                accessTokenExp: accessTokenPayload.exp
            });
            
            // Extract and store user info
            const userInfo = extractUserFromToken(idToken);
            setUser(userInfo);
            setUserGroups(userInfo?.groups || []);
            setIsAuthenticated(true);
            setAuthError(null);
            
        } catch (err) {
            console.error('Error storing tokens:', err);
            setAuthError('Failed to process authentication');
        }
    }, [extractUserFromToken]);

    /**
     * Clear all authentication state
     * Called on logout, session expiry, or auth errors
     */
    const clearAuth = useCallback(() => {
        if (!isMounted.current) return;
        
        setTokens(null);
        setUser(null);
        setUserGroups([]);
        setIsAuthenticated(false);
        setAuthError(null);
    }, []);

    /**
     * Check if token is still valid (not expired)
     */
    const isTokenValid = useCallback((expTimestamp) => {
        if (!expTimestamp) return false;
        const now = Math.floor(Date.now() / 1000);
        return (expTimestamp - now) > TOKEN_EXPIRY_BUFFER_SECONDS;
    }, []);

    /**
     * Get a fresh access token
     * Refreshes from Cognito if current token is expired or about to expire
     */
    const getAccessToken = useCallback(async () => {
        try {
            // Check if we have a valid token in memory
            if (tokens?.accessToken && isTokenValid(tokens.accessTokenExp)) {
                return tokens.accessToken;
            }
            
            // Token expired or missing - refresh from Cognito
            const session = await Auth.currentSession();
            const newAccessToken = session.getAccessToken().getJwtToken();
            
            if (isMounted.current) {
                // Store full tokens from session (avoids partial state when prev is null)
                storeTokens(session);
            }
            
            return newAccessToken;
        } catch (err) {
            console.error('Error getting access token:', err);
            // Do NOT clearAuth here - avoids race/transient errors logging user out (e.g. new tab, slow session)
            return null;
        }
    }, [tokens, isTokenValid, storeTokens]);

    /**
     * Get the ID token (for user info)
     */
    const getIdToken = useCallback(async () => {
        try {
            if (tokens?.idToken && isTokenValid(tokens.idTokenExp)) {
                return tokens.idToken;
            }
            
            const session = await Auth.currentSession();
            const newIdToken = session.getIdToken().getJwtToken();
            
            if (isMounted.current) {
                // Store full tokens from session (avoids partial state when prev is null)
                storeTokens(session);
            }
            
            return newIdToken;
        } catch (err) {
            console.error('Error getting ID token:', err);
            // Do NOT clearAuth here - avoids race/transient errors logging user out
            return null;
        }
    }, [tokens, isTokenValid, storeTokens]);

    /**
     * Get decoded ID token payload
     */
    const getDecodedIdToken = useCallback(async () => {
        const idToken = await getIdToken();
        if (!idToken) return null;
        
        try {
            return jwt(idToken);
        } catch (err) {
            console.error('Error decoding ID token:', err);
            return null;
        }
    }, [getIdToken]);

    /**
     * Logout - Clear Cognito session and all local auth state
     */
    const logout = useCallback(async () => {
        try {
            await Auth.signOut();
        } catch (err) {
            console.error('Error during logout:', err);
        } finally {
            clearAuth();
            clearAuthTokenCache();
            // Note: We intentionally do NOT call localStorage.clear()
            // as we're not using localStorage for auth anymore
        }
    }, [clearAuth]);

    /**
     * Handle successful login
     * Called after Amplify authenticator completes
     */
    const handleLoginSuccess = useCallback(async () => {
        try {
            const session = await Auth.currentSession();
            storeTokens(session);
        } catch (err) {
            console.error('Error handling login:', err);
            setAuthError('Login failed');
        }
    }, [storeTokens]);

    /**
     * Check for existing session on mount (runs once per mount; avoids duplicate runs from Strict Mode)
     */
    useEffect(() => {
        isMounted.current = true;
        if (sessionCheckInProgress.current) return;
        sessionCheckInProgress.current = true;

        const checkExistingSession = async () => {
            try {
                const session = await Auth.currentSession();
                if (isMounted.current) {
                    storeTokens(session);
                }
            } catch (err) {
                if (isMounted.current) {
                    clearAuth();
                }
            } finally {
                sessionCheckInProgress.current = false;
                // Always clear loading when check completes so PrivateRoute (e.g. messages tab) doesn't show spinner forever
                setIsLoading(false);
            }
        };

        checkExistingSession();

        return () => {
            isMounted.current = false;
        };
    }, [storeTokens, clearAuth]);

    /**
     * Context value - all auth-related state and methods
     */
    const contextValue = {
        // State
        tokens,
        user,
        userGroups,
        isAuthenticated,
        isLoading,
        authError,
        
        // Methods
        getAccessToken,
        getIdToken,
        getDecodedIdToken,
        storeTokens,
        clearAuth,
        logout,
        handleLoginSuccess,
        
        // Utility
        isTokenValid
    };

    return (
        <AuthContext.Provider value={contextValue}>
            {children}
        </AuthContext.Provider>
    );
};

export default AuthContext;
